import React from 'react';

import { Badge, Callout, Step } from './shared';

const ConnectJira: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-4">Connect Jira</h2>
    <p className="text-sm text-base-content/70 mb-5 leading-relaxed">
      Jira is MFlow&apos;s primary data source. Connecting it enables project syncing, PSI computation, risk flag
      detection, and AI-powered sprint analysis. You only need to connect once per workspace.
    </p>

    <h3 className="font-semibold mb-3">Step-by-step</h3>
    <div className="space-y-3 mb-6">
      <Step n={1}>
        Open <strong>Integrations</strong> from the sidebar (plug icon) or navigate to{' '}
        <code className="bg-base-200 px-1 rounded text-xs">/integrations</code>.
      </Step>
      <Step n={2}>
        Find the <strong>Jira</strong> card and click <strong>Connect</strong>. A popup window will open redirecting you
        to Atlassian&apos;s OAuth consent screen.
      </Step>
      <Step n={3}>
        Log in to Atlassian if prompted, then click <strong>Accept</strong> to grant MFlow access to your Jira
        workspace. The popup will close automatically once authorization succeeds.
      </Step>
      <Step n={4}>
        Back on the Integrations page the Jira card shows <Badge color="badge-success">Connected</Badge>. MFlow will
        list all projects available in your workspace.
      </Step>
      <Step n={5}>
        Click <strong>Change</strong> under <em>Synced projects</em> to choose which Jira projects to import. Select one
        or more, pick a <strong>sync history window</strong> (how far back to import changelog data), and click{' '}
        <strong>Save</strong>.
      </Step>
      <Step n={6}>
        Click <strong>Sync now</strong> to kick off the first import. A progress indicator will appear in the sidebar
        while data is being processed.
      </Step>
    </div>

    <Callout type="info">
      By default MFlow only <strong>reads</strong> from Jira. The AI agent can also <strong>write</strong> — create
      issues, update sprints, change statuses — but only when you explicitly ask it to. No changes are made to your
      workspace without an instruction from you.
    </Callout>

    <h3 className="font-semibold mt-5 mb-3">Permissions requested</h3>
    <ul className="text-sm text-base-content/70 space-y-1 list-disc list-inside mb-4">
      <li>View and read Jira projects, boards, and issues</li>
      <li>View sprints and changelog history</li>
      <li>View filters and comments (used for context in AI answers)</li>
    </ul>

    <h3 className="font-semibold mb-3">Managing the sync history window</h3>
    <p className="text-sm text-base-content/70 mb-2 leading-relaxed">
      When saving projects you choose how far back to import changelog data:
    </p>
    <div className="overflow-x-auto">
      <table className="table table-sm text-sm">
        <thead>
          <tr>
            <th>Preset</th>
            <th>Best for</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Last month', 'Quick check — minimal data, fast sync.'],
            ['Last 3 months', 'Recent sprint trends.'],
            ['Last 6 months', 'Quarterly planning context.'],
            ['Last year (default)', 'Recommended for most teams.'],
            ['Last 2 years', 'Long-running projects with historical baselines.'],
          ].map(([p, d]) => (
            <tr key={p}>
              <td className="whitespace-nowrap font-medium">{p}</td>
              <td className="text-base-content/60">{d}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <Callout type="warning">
      Importing more history increases the first sync time. For large workspaces with thousands of issues, &quot;Last 2
      years&quot; may take several minutes.
    </Callout>

    <h3 className="font-semibold mt-5 mb-3">Disconnecting Jira</h3>
    <p className="text-sm text-base-content/70 leading-relaxed">
      Click <strong>Disconnect</strong> on the Jira card. All synced project data and tokens are removed from
      MFlow&apos;s servers. Your Jira workspace is not affected.
    </p>
  </div>
);

export default ConnectJira;
